import styled from '@emotion/styled';
import { theme } from '../../styles/theme';

/**
 * Minimum comfortable touch target. 44px is Apple's HIG floor (Material asks
 * 48dp); before this the result chips were 20px tall and `Ball` was 25x20,
 * which is a thumb-sized problem on a phone at a ballpark.
 *
 * Applied at every width on purpose: the 768px+ layout is also what a tablet
 * gets, and a floor that stops at the phone breakpoint leaves exactly the
 * devices most likely to be used for charting with 20px targets.
 *
 * Currently spread into the result chips only. The rest of the in-game controls
 * (pitch-type buttons, Stats/Change, Undo, the toolbar chips) still need it, but
 * raising them costs more vertical space than the screen has left — see the
 * change doc for the measured budget. Spread this rather than restating the
 * numbers per component, so the floor cannot drift back down one button at a
 * time as the remaining controls are converted.
 */
const touchTarget = {
    minHeight: '44px',
    // Both dimensions, not just height: `Ball` was 25px wide, so a height-only
    // floor would still leave a 25x44 sliver to hit.
    minWidth: '44px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
} as const;

// Styled Components
export const Container = styled.div({
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: theme.surfaces.body,

    [`@media (min-width: ${theme.breakpoints.lg})`]: {
        display: 'grid',
        gridTemplateColumns: '350px 1fr',
    },
});

export const LeftPanel = styled.div({
    backgroundColor: theme.surfaces.card,
    borderBottom: `1px solid ${theme.colors.gray[200]}`,
    display: 'none', // Hidden on mobile by default
    flexDirection: 'column',
    gap: theme.spacing.md,
    padding: theme.spacing.md,

    [`@media (min-width: ${theme.breakpoints.lg})`]: {
        display: 'flex',
        borderRight: `1px solid ${theme.colors.gray[200]}`,
        borderBottom: 'none',
        overflowY: 'auto',
        position: 'sticky',
        top: 0,
        height: '100vh',
    },
});

export const MainPanel = styled.div({
    padding: theme.spacing.sm,
    overflowY: 'auto',
    flex: 1,

    [`@media (min-width: ${theme.breakpoints.md})`]: {
        padding: theme.spacing.lg,
    },
});

export const GameHeader = styled.div({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing.xs,
    background: `linear-gradient(135deg, ${theme.colors.primary[600]} 0%, ${theme.colors.primary[800]} 100%)`,
    borderRadius: theme.borderRadius.md,
    marginBottom: theme.spacing.sm,
    boxShadow: theme.shadows.md,
    gap: theme.spacing.xs,

    [`@media (min-width: ${theme.breakpoints.sm})`]: {
        padding: theme.spacing.sm,
        gap: theme.spacing.sm,
    },
});

export const TeamInfo = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing.sm,
});

export const TeamName = styled.div({
    color: 'white',
    fontSize: theme.fontSize.sm,
    fontWeight: theme.fontWeight.semibold,
    textAlign: 'center',
    maxWidth: '80px',
    wordWrap: 'break-word',

    [`@media (min-width: ${theme.breakpoints.sm})`]: {
        fontSize: theme.fontSize.lg,
        maxWidth: 'none',
    },
});

export const Score = styled.div({
    color: 'white',
    fontSize: theme.fontSize['xl'],
    fontWeight: theme.fontWeight.bold,
});

export const SwapButton = styled.button({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.xs,
    padding: `${theme.spacing.xs} ${theme.spacing.md}`,
    backgroundColor: 'transparent',
    color: theme.colors.gray[600],
    border: `1px solid ${theme.colors.gray[300]}`,
    borderRadius: theme.borderRadius.full,
    fontSize: theme.fontSize.sm,
    fontWeight: theme.fontWeight.medium,
    cursor: 'pointer',
    transition: 'all 0.2s',
    flexShrink: 0,
    whiteSpace: 'nowrap',

    '&:hover': {
        backgroundColor: theme.colors.gray[100],
        color: theme.colors.gray[800],
    },
});

export const GameInfo = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing.xs,
});

export const Inning = styled.div({
    color: 'white',
    fontSize: theme.fontSize.base,
    fontWeight: theme.fontWeight.semibold,
});

export const InningHalf = styled.div({
    color: theme.colors.primary[100],
    fontSize: theme.fontSize.sm,
    textTransform: 'uppercase',
});

export const CountDisplay = styled.div({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.sm,
    padding: theme.spacing.xs,
    backgroundColor: theme.surfaces.card,
    borderRadius: theme.borderRadius.md,
    marginBottom: theme.spacing.xs,
    boxShadow: theme.shadows.sm,

    [`@media (min-width: ${theme.breakpoints.sm})`]: {
        gap: theme.spacing.md,
    },
});

export const CountLabel = styled.span({
    fontSize: theme.fontSize.base,
    color: theme.colors.gray[600],
    fontWeight: theme.fontWeight.medium,
});

export const CountValue = styled.span({
    fontSize: theme.fontSize['2xl'],
    fontWeight: theme.fontWeight.bold,
    color: theme.colors.primary[600],
});

export const OutsDisplay = styled.span({
    fontSize: theme.fontSize.base,
    color: theme.colors.gray[700],
    fontWeight: theme.fontWeight.medium,
});

export const StrikeZoneRow = styled.div({
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: theme.spacing.md,
    marginBottom: theme.spacing.md,

    [`@media (min-width: ${theme.breakpoints.md})`]: {
        gridTemplateColumns: '1fr 1fr',
        alignItems: 'start',
    },
});

export const StrikeZoneContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
});

export const PitchForm = styled.div({
    padding: theme.spacing.lg,
    backgroundColor: theme.surfaces.card,
    borderRadius: theme.borderRadius.xl,
    boxShadow: theme.shadows.md,
});

export const FormRow = styled.div({
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing.md,
    marginBottom: theme.spacing.lg,
});

export const FormGroup = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xs,
});

export const Label = styled.label({
    fontSize: theme.fontSize.xs,
    fontWeight: theme.fontWeight.semibold,
    color: theme.colors.gray[700],
});

// "Step 3: Result" — the same step scaffolding dropped from the pitch-type
// block on phones. The color-coded result buttons sitting under the zone need
// no heading; the velocity Label above keeps its own, since that one labels an
// actual input.
export const ResultLabel = styled(Label)({
    display: 'none',

    [`@media (min-width: ${theme.breakpoints.md})`]: {
        display: 'block',
    },
});

export const Select = styled.select({
    padding: theme.spacing.sm,
    border: `1px solid ${theme.colors.gray[300]}`,
    borderRadius: theme.borderRadius.sm,
    fontSize: theme.fontSize.sm,

    '&:focus': {
        outline: 'none',
        borderColor: theme.colors.primary[500],
        boxShadow: `0 0 0 2px ${theme.colors.primary[100]}`,
    },
});

export const Input = styled.input({
    padding: theme.spacing.sm,
    border: `1px solid ${theme.colors.gray[300]}`,
    borderRadius: theme.borderRadius.sm,
    fontSize: theme.fontSize.sm,

    '&:focus': {
        outline: 'none',
        borderColor: theme.colors.primary[500],
        boxShadow: `0 0 0 2px ${theme.colors.primary[100]}`,
    },
});

export const ResultButtons = styled.div({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing.xs,

    [`@media (min-width: ${theme.breakpoints.sm})`]: {
        gap: theme.spacing.sm,
    },
});

export const ResultButton = styled.button<{ active: boolean; color: string }>((props) => ({
    ...touchTarget,
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
    backgroundColor: props.active ? props.color : theme.colors.gray[100],
    color: props.active ? 'white' : theme.colors.gray[700],
    border: `1px solid ${props.active ? props.color : theme.colors.gray[300]}`,
    borderRadius: theme.borderRadius.sm,
    fontSize: theme.fontSize.xs,
    fontWeight: theme.fontWeight.medium,
    cursor: 'pointer',
    transition: 'all 0.2s',

    '&:hover': {
        backgroundColor: props.active ? props.color : theme.colors.gray[200],
    },
}));

export const LogRow = styled.div({
    display: 'flex',
    gap: theme.spacing.sm,
    alignItems: 'stretch',
});

export const UndoButton = styled.button({
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    backgroundColor: theme.surfaces.card,
    color: theme.colors.red[700],
    border: `1px solid ${theme.colors.red[600]}`,
    borderRadius: theme.borderRadius.sm,
    fontSize: theme.fontSize.base,
    fontWeight: theme.fontWeight.semibold,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    marginTop: theme.spacing.md,
    whiteSpace: 'nowrap',

    '&:hover': {
        backgroundColor: theme.colors.red[50],
    },
});

export const EndAtBatButton = styled.button({
    width: '100%',
    padding: theme.spacing.sm,
    backgroundColor: theme.colors.green[600],
    color: 'white',
    border: 'none',
    borderRadius: theme.borderRadius.sm,
    fontSize: theme.fontSize.sm,
    fontWeight: theme.fontWeight.semibold,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    marginTop: theme.spacing.sm,

    '&:hover': {
        backgroundColor: theme.colors.green[700],
    },
});

export const NoAtBatContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing['3xl'],
    backgroundColor: theme.surfaces.card,
    borderRadius: theme.borderRadius.xl,
    boxShadow: theme.shadows.md,
});

export const NoAtBatText = styled.p({
    fontSize: theme.fontSize.xl,
    color: theme.colors.gray[600],
    marginBottom: theme.spacing.lg,
});

export const StartAtBatButton = styled.button({
    padding: `${theme.spacing.md} ${theme.spacing.xl}`,
    backgroundColor: theme.colors.primary[600],
    color: 'white',
    border: 'none',
    borderRadius: theme.borderRadius.md,
    fontSize: theme.fontSize.lg,
    fontWeight: theme.fontWeight.semibold,
    cursor: 'pointer',
    transition: 'background-color 0.2s',

    '&:hover': {
        backgroundColor: theme.colors.primary[700],
    },
});

export const LoadingContainer = styled.div({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    fontSize: theme.fontSize.xl,
    color: theme.colors.gray[600],
});

export const ErrorContainer = styled.div({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    fontSize: theme.fontSize.xl,
    color: theme.colors.red[600],
});

export const PlayerDisplay = styled.div({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    // Grid items default to min-content width; without this a long player name
    // would widen the card instead of truncating and push the pair onto two
    // lines, undoing the space PlayersRow just bought.
    minWidth: 0,
    padding: theme.spacing.sm,
    backgroundColor: theme.surfaces.card,
    borderRadius: theme.borderRadius.lg,
    boxShadow: theme.shadows.sm,
    gap: theme.spacing.sm,

    [`@media (min-width: ${theme.breakpoints.sm})`]: {
        padding: theme.spacing.md,
    },
});

export const PlayerInfo = styled.div({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
    minWidth: 0,
    flex: 1,

    [`@media (min-width: ${theme.breakpoints.sm})`]: {
        gap: theme.spacing.md,
    },
});

export const PlayerLabel = styled.span({
    fontSize: theme.fontSize.xs,
    fontWeight: theme.fontWeight.semibold,
    color: theme.colors.gray[500],
    textTransform: 'uppercase',
});

export const PlayerName = styled.span({
    fontSize: theme.fontSize.sm,
    fontWeight: theme.fontWeight.semibold,
    color: theme.colors.gray[900],
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',

    [`@media (min-width: ${theme.breakpoints.sm})`]: {
        fontSize: theme.fontSize.lg,
    },
});

export const PlayerNumber = styled.span({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    backgroundColor: theme.colors.primary[100],
    color: theme.colors.primary[700],
    fontWeight: theme.fontWeight.bold,
    fontSize: theme.fontSize.sm,
    flexShrink: 0,

    [`@media (min-width: ${theme.breakpoints.sm})`]: {
        width: '36px',
        height: '36px',
        fontSize: theme.fontSize.base,
    },
});

export const ChangeButton = styled.button({
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
    backgroundColor: theme.colors.gray[100],
    color: theme.colors.gray[700],
    border: `1px solid ${theme.colors.gray[300]}`,
    borderRadius: theme.borderRadius.md,
    fontSize: theme.fontSize.xs,
    cursor: 'pointer',
    flexShrink: 0,

    '&:hover:not(:disabled)': {
        backgroundColor: theme.colors.gray[200],
    },

    '&:disabled': {
        opacity: 0.5,
        cursor: 'not-allowed',
    },

    [`@media (min-width: ${theme.breakpoints.sm})`]: {
        padding: `${theme.spacing.xs} ${theme.spacing.md}`,
        fontSize: theme.fontSize.sm,
    },
});

// Stacked on phones, side by side from `sm`. Putting the two cards side by side
// on a phone would free 32px, but at 390px the pitcher's name truncates to
// "G.." once the Stats/Change buttons take their share — so that 32px is not
// available until the pitcher/batter/count status-line redesign lands.
export const PlayersRow = styled.div({
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: theme.spacing.sm,
    marginBottom: theme.spacing.md,

    [`@media (min-width: ${theme.breakpoints.sm})`]: {
        gridTemplateColumns: '1fr 1fr',
        gap: theme.spacing.md,
        marginBottom: theme.spacing.lg,
    },
});

export const SetupPrompt = styled.div({
    textAlign: 'center',
    padding: theme.spacing.xl,
    backgroundColor: theme.colors.yellow[50],
    border: `1px solid ${theme.colors.yellow[200]}`,
    borderRadius: theme.borderRadius.lg,
    marginBottom: theme.spacing.lg,
});

export const SetupText = styled.p({
    fontSize: theme.fontSize.base,
    color: theme.colors.yellow[800],
    marginBottom: theme.spacing.md,
});

export const SetupButton = styled.button({
    padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
    backgroundColor: theme.colors.yellow[500],
    color: 'white',
    border: 'none',
    borderRadius: theme.borderRadius.md,
    fontSize: theme.fontSize.sm,
    fontWeight: theme.fontWeight.semibold,
    cursor: 'pointer',

    '&:hover': {
        backgroundColor: theme.colors.yellow[600],
    },
});

// Outs + base-runner diamond, the tall part of the scoreboard column. On a
// phone the diamond is scaled down and the gaps tightened; BaseRunnerDisplay
// sizes itself from a px prop, so the override has to reach its container.
export const OutsAndBasesRow = styled.div({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.md,
    marginTop: theme.spacing.sm,

    '& > div:last-of-type': {
        width: '34px',
        height: '34px',
    },

    [`@media (min-width: ${theme.breakpoints.md})`]: {
        gap: theme.spacing.xl,
        marginTop: theme.spacing.md,
        '& > div:last-of-type': {
            width: '50px',
            height: '50px',
        },
    },
});

// One row at every width. On a phone the back button collapses to its arrow
// (see BackLabel) and the action chips scroll horizontally instead of wrapping
// to a second line, which keeps the whole bar to a single row — 31px of the
// vertical budget that the pitch loop needs below.
export const TopBar = styled.div({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.sm,
    marginBottom: theme.spacing.md,

    [`@media (min-width: ${theme.breakpoints.sm})`]: {
        justifyContent: 'space-between',
        marginBottom: theme.spacing.lg,
    },
});

// The "Back to Dashboard" wording, dropped on phones so the button is just the
// arrow. The arrow stays outside this span so the control never disappears.
export const BackLabel = styled.span({
    display: 'none',

    [`@media (min-width: ${theme.breakpoints.md})`]: {
        display: 'inline',
    },
});

export const BackButton = styled.button({
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    gap: theme.spacing.sm,
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    backgroundColor: 'transparent',
    color: theme.colors.gray[600],
    border: `1px solid ${theme.colors.gray[300]}`,
    borderRadius: theme.borderRadius.md,
    fontSize: theme.fontSize.sm,
    cursor: 'pointer',

    '&:hover': {
        backgroundColor: theme.colors.gray[100],
        color: theme.colors.gray[800],
    },
});

export const GameStatus = styled.span<{ status?: string }>((props) => ({
    padding: `${theme.spacing.xs} ${theme.spacing.md}`,
    borderRadius: theme.borderRadius.full,
    fontSize: theme.fontSize.sm,
    fontWeight: theme.fontWeight.semibold,
    backgroundColor:
        props.status === 'in_progress'
            ? theme.colors.green[100]
            : props.status === 'completed'
              ? theme.colors.gray[200]
              : theme.colors.yellow[100],
    color:
        props.status === 'in_progress'
            ? theme.colors.green[700]
            : props.status === 'completed'
              ? theme.colors.gray[700]
              : theme.colors.yellow[700],
}));

export const StartGameButton = styled.button({
    padding: `${theme.spacing.md} ${theme.spacing.xl}`,
    backgroundColor: theme.colors.green[600],
    color: 'white',
    border: 'none',
    borderRadius: theme.borderRadius.md,
    fontSize: theme.fontSize.lg,
    fontWeight: theme.fontWeight.semibold,
    cursor: 'pointer',
    transition: 'background-color 0.2s',

    '&:hover': {
        backgroundColor: theme.colors.green[700],
    },
});

export const TopBarRight = styled.div({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
    // Phones scroll the chips sideways rather than wrapping them onto a second
    // row; nothing is removed, it just stops costing vertical space. Wrapping
    // returns at `sm`, where there is room for it.
    flexWrap: 'nowrap',
    overflowX: 'auto',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': { display: 'none' },
    '& > *': { flexShrink: 0 },

    [`@media (min-width: ${theme.breakpoints.sm})`]: {
        gap: theme.spacing.md,
        flexWrap: 'wrap',
        overflowX: 'visible',
    },
});

export const EndGameButton = styled.button({
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    backgroundColor: theme.accents.red,
    color: 'white',
    border: 'none',
    borderRadius: theme.borderRadius.md,
    fontSize: theme.fontSize.sm,
    fontWeight: theme.fontWeight.semibold,
    cursor: 'pointer',
    transition: 'background-color 0.2s',

    '&:hover': {
        backgroundColor: theme.accents.redHover,
    },
});

export const ResumeGameButton = styled.button({
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    backgroundColor: theme.colors.green[600],
    color: 'white',
    border: 'none',
    borderRadius: theme.borderRadius.md,
    fontSize: theme.fontSize.sm,
    fontWeight: theme.fontWeight.semibold,
    cursor: 'pointer',
    transition: 'background-color 0.2s',

    '&:hover': {
        backgroundColor: theme.colors.green[700],
    },
});

export const StartGamePrompt = styled.div({
    textAlign: 'center',
    padding: theme.spacing['2xl'],
    backgroundColor: theme.surfaces.card,
    borderRadius: theme.borderRadius.xl,
    boxShadow: theme.shadows.md,
    marginBottom: theme.spacing.xl,
});

export const StartGameText = styled.p({
    fontSize: theme.fontSize.lg,
    color: theme.colors.gray[600],
    marginBottom: theme.spacing.lg,
});

// Outs display with visual indicators
export const OutsContainer = styled.div({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
});

export const OutsLabel = styled.span({
    fontSize: theme.fontSize.xs,
    color: theme.colors.primary[200],
    textTransform: 'uppercase',
    marginRight: theme.spacing.xs,
});

export const OutIndicator = styled.div<{ active: boolean }>((props) => ({
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    backgroundColor: props.active ? theme.colors.yellow[400] : 'rgba(255, 255, 255, 0.3)',
    border: `2px solid ${props.active ? theme.colors.yellow[500] : 'rgba(255, 255, 255, 0.4)'}`,
    transition: 'all 0.2s',
}));

export const OutsNumber = styled.span({
    fontSize: theme.fontSize.base,
    fontWeight: theme.fontWeight.bold,
    color: theme.colors.yellow[400],
    marginLeft: theme.spacing.xs,
});

// At-bat result buttons for in-play scenarios
export const InPlayResultsContainer = styled.div({
    marginTop: theme.spacing.md,
    padding: theme.spacing.md,
    backgroundColor: theme.colors.gray[50],
    borderRadius: theme.borderRadius.md,
    border: `1px solid ${theme.colors.gray[200]}`,
});

export const InPlayResultsTitle = styled.div({
    fontSize: theme.fontSize.sm,
    fontWeight: theme.fontWeight.semibold,
    color: theme.colors.gray[700],
    marginBottom: theme.spacing.sm,
});

export const InPlayResultsGrid = styled.div({
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: theme.spacing.sm,
});

export const InPlayResultButton = styled.button<{ isOut?: boolean }>((props) => ({
    padding: theme.spacing.sm,
    backgroundColor: props.isOut ? theme.colors.red[50] : theme.colors.green[50],
    color: props.isOut ? theme.colors.red[700] : theme.colors.green[700],
    border: `1px solid ${props.isOut ? theme.colors.red[200] : theme.colors.green[200]}`,
    borderRadius: theme.borderRadius.md,
    fontSize: theme.fontSize.xs,
    fontWeight: theme.fontWeight.medium,
    cursor: 'pointer',
    transition: 'all 0.2s',

    '&:hover': {
        backgroundColor: props.isOut ? theme.colors.red[100] : theme.colors.green[100],
        borderColor: props.isOut ? theme.colors.red[300] : theme.colors.green[300],
    },
}));

// Inning change notification
export const InningChangeOverlay = styled.div({
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
});

export const InningChangeModal = styled.div({
    backgroundColor: theme.surfaces.card,
    padding: theme.spacing['2xl'],
    borderRadius: theme.borderRadius.xl,
    textAlign: 'center',
    boxShadow: theme.shadows.xl,
});

export const InningChangeText = styled.div({
    fontSize: theme.fontSize['2xl'],
    fontWeight: theme.fontWeight.bold,
    color: theme.colors.gray[900],
    marginBottom: theme.spacing.md,
});

export const InningChangeSubtext = styled.div({
    fontSize: theme.fontSize.base,
    color: theme.colors.gray[600],
    marginBottom: theme.spacing.lg,
});

export const InningChangeDismiss = styled.button({
    padding: `${theme.spacing.sm} ${theme.spacing.xl}`,
    backgroundColor: theme.colors.primary[600],
    color: 'white',
    border: 'none',
    borderRadius: theme.borderRadius.md,
    fontSize: theme.fontSize.base,
    fontWeight: theme.fontWeight.semibold,
    cursor: 'pointer',

    '&:hover': {
        backgroundColor: theme.colors.primary[700],
    },
});

export const RunsInputSection = styled.div({
    marginBottom: theme.spacing.lg,
});

export const RunsInputLabel = styled.label({
    display: 'block',
    fontSize: theme.fontSize.sm,
    fontWeight: theme.fontWeight.medium,
    color: theme.colors.gray[700],
    marginBottom: theme.spacing.sm,
});

export const RunsInput = styled.input({
    width: '80px',
    padding: theme.spacing.sm,
    fontSize: theme.fontSize.xl,
    fontWeight: theme.fontWeight.bold,
    textAlign: 'center',
    border: `2px solid ${theme.colors.gray[300]}`,
    borderRadius: theme.borderRadius.md,

    '&:focus': {
        outline: 'none',
        borderColor: theme.colors.primary[500],
        boxShadow: `0 0 0 3px ${theme.colors.primary[100]}`,
    },
});

// Baseball Diamond Modal for In-Play recording
export const DiamondModalOverlay = styled.div({
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.78)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
});

export const DiamondModal = styled.div({
    backgroundColor: theme.surfaces.card,
    padding: theme.spacing.xl,
    borderRadius: theme.borderRadius.xl,
    border: `1px solid ${theme.surfaces.border}`,
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.6)',
    maxWidth: '500px',
    width: '90%',
    maxHeight: '90vh',
    overflowY: 'auto',
});

export const DiamondModalHeader = styled.div({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing.lg,
});

export const DiamondModalTitle = styled.h3({
    fontSize: theme.fontSize.xl,
    fontWeight: theme.fontWeight.bold,
    color: theme.colors.gray[900],
    margin: 0,
});

export const DiamondModalClose = styled.button({
    padding: theme.spacing.sm,
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: theme.fontSize.xl,
    color: theme.colors.gray[500],
    cursor: 'pointer',

    '&:hover': {
        color: theme.colors.gray[700],
    },
});

export const HitTypeSelector = styled.div({
    display: 'flex',
    gap: theme.spacing.sm,
    marginBottom: theme.spacing.lg,
});

export const HitTypeButton = styled.button<{ active: boolean; hitColor: string }>((props) => ({
    flex: 1,
    padding: theme.spacing.sm,
    backgroundColor: props.active ? props.hitColor : theme.surfaces.card,
    color: props.active ? 'white' : theme.colors.gray[700],
    border: `2px solid ${props.active ? props.hitColor : theme.surfaces.border}`,
    borderRadius: theme.borderRadius.md,
    fontSize: theme.fontSize.sm,
    fontWeight: theme.fontWeight.medium,
    cursor: 'pointer',
    transition: 'all 0.2s',

    '&:hover': {
        backgroundColor: props.active ? props.hitColor : theme.colors.gray[100],
        borderColor: props.active ? props.hitColor : theme.surfaces.borderStrong,
    },
}));

export const DiamondInstructions = styled.p({
    fontSize: theme.fontSize.sm,
    color: theme.colors.gray[600],
    textAlign: 'center',
    marginBottom: theme.spacing.md,
});

export const DiamondContainer = styled.div({
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing.lg,
});

export const DiamondResultSection = styled.div({
    borderTop: `1px solid ${theme.colors.gray[200]}`,
    paddingTop: theme.spacing.lg,
});

export const DiamondResultTitle = styled.div({
    fontSize: theme.fontSize.xs,
    fontWeight: theme.fontWeight.semibold,
    color: theme.colors.gray[500],
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: theme.spacing.sm,
});

export const DiamondResultGrid = styled.div({
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: theme.spacing.sm,
});

export const DiamondResultButton = styled.button<{ isOut?: boolean; disabled?: boolean }>((props) => ({
    padding: theme.spacing.sm,
    backgroundColor: props.disabled ? theme.surfaces.card : props.isOut ? theme.colors.red[50] : theme.colors.green[50],
    color: props.disabled ? theme.colors.gray[400] : props.isOut ? theme.colors.red[700] : theme.colors.green[700],
    border: `1px solid ${props.disabled ? theme.surfaces.border : props.isOut ? theme.colors.red[300] : theme.colors.green[300]}`,
    borderRadius: theme.borderRadius.md,
    fontSize: theme.fontSize.xs,
    fontWeight: theme.fontWeight.medium,
    cursor: props.disabled ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s',
    opacity: props.disabled ? 0.45 : 1,

    '&:hover:not(:disabled)': {
        backgroundColor: props.isOut ? theme.colors.red[100] : theme.colors.green[100],
        borderColor: props.isOut ? theme.colors.red[400] : theme.colors.green[400],
    },
}));

export const OpenDiamondButton = styled.button({
    width: '100%',
    padding: theme.spacing.md,
    backgroundColor: theme.accents.violet,
    color: 'white',
    border: 'none',
    borderRadius: theme.borderRadius.md,
    fontSize: theme.fontSize.base,
    fontWeight: theme.fontWeight.semibold,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    marginTop: theme.spacing.md,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing.sm,

    '&:hover': {
        backgroundColor: theme.accents.violetHover,
    },
});

// Pitch Flow Step Indicator
export const PitchFlowContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.md,
});

export const StepIndicator = styled.div({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
    backgroundColor: theme.surfaces.card,
    borderRadius: theme.borderRadius.md,
    boxShadow: theme.shadows.sm,
    marginBottom: theme.spacing.sm,
    overflowX: 'auto',

    [`@media (min-width: ${theme.breakpoints.sm})`]: {
        padding: `${theme.spacing.sm} ${theme.spacing.md}`,
    },
});

export const Step = styled.div<{ active?: boolean; completed?: boolean }>((props) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
    opacity: props.active || props.completed ? 1 : 0.4,
}));

export const StepNumber = styled.div<{ active?: boolean; completed?: boolean }>((props) => ({
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: theme.fontSize.xs,
    fontWeight: theme.fontWeight.bold,
    backgroundColor: props.completed ? theme.colors.green[500] : props.active ? theme.colors.primary[600] : theme.colors.gray[200],
    color: props.completed || props.active ? 'white' : theme.colors.gray[500],
    transition: 'all 0.2s',
    flexShrink: 0,

    [`@media (min-width: ${theme.breakpoints.sm})`]: {
        width: '22px',
        height: '22px',
    },
}));

export const StepLabel = styled.span<{ active?: boolean }>((props) => ({
    fontSize: theme.fontSize.sm,
    fontWeight: props.active ? theme.fontWeight.semibold : theme.fontWeight.medium,
    color: props.active ? theme.colors.gray[900] : theme.colors.gray[600],

    [`@media (max-width: ${theme.breakpoints.md})`]: {
        display: 'none',
    },
}));

export const StepConnector = styled.div<{ completed?: boolean }>((props) => ({
    flex: 1,
    height: '2px',
    backgroundColor: props.completed ? theme.colors.green[500] : theme.colors.gray[200],
    margin: `0 ${theme.spacing.xs}`,
    minWidth: '8px',
    transition: 'all 0.2s',

    [`@media (min-width: ${theme.breakpoints.sm})`]: {
        margin: `0 ${theme.spacing.sm}`,
        minWidth: '16px',
    },
}));

// Pitch Type Selector (prominent display)
export const PitchTypeSelector = styled.div({
    backgroundColor: theme.surfaces.card,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.sm,
    boxShadow: theme.shadows.sm,
    marginBottom: theme.spacing.sm,

    [`@media (min-width: ${theme.breakpoints.sm})`]: {
        padding: theme.spacing.md,
    },
});

// "Step 1: Select Pitch Type" — step scaffolding that a row of pitch-type
// buttons does not need on a phone, where the vertical space is worth more.
export const PitchTypeSelectorTitle = styled.div({
    display: 'none',
    fontSize: theme.fontSize.xs,
    fontWeight: theme.fontWeight.semibold,
    color: theme.colors.gray[700],
    marginBottom: theme.spacing.sm,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',

    [`@media (min-width: ${theme.breakpoints.md})`]: {
        display: 'block',
    },
});

export const PitchTypeGrid = styled.div({
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(70px, 1fr))',
    gap: theme.spacing.xs,

    [`@media (min-width: ${theme.breakpoints.sm})`]: {
        gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))',
        gap: theme.spacing.sm,
    },
});

// `tint` encodes strike% vs current batter hand. Null/undefined → neutral (sample too small, n<15).
export const PitchTypeButton = styled.button<{ active?: boolean; tint?: string | null }>((props) => ({
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
    backgroundColor: props.active ? theme.accents.blue : props.tint || theme.colors.gray[50],
    color: props.active ? 'white' : theme.colors.gray[700],
    border: `1px solid ${props.active ? theme.accents.blue : props.tint ? 'transparent' : theme.colors.gray[200]}`,
    borderRadius: theme.borderRadius.sm,
    fontSize: theme.fontSize.xs,
    fontWeight: theme.fontWeight.medium,
    cursor: 'pointer',
    transition: 'all 0.15s',

    '&:hover': {
        backgroundColor: props.active ? theme.accents.blueHover : props.tint || theme.colors.gray[100],
        borderColor: props.active ? theme.accents.blueHover : props.tint ? 'transparent' : theme.colors.gray[300],
    },
}));

// Zone instruction overlay
export const ZoneInstructionOverlay = styled.div<{ visible?: boolean }>((props) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: props.visible ? 'flex' : 'none',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: theme.borderRadius.lg,
    pointerEvents: 'none',
}));

export const ZoneInstructionText = styled.div({
    color: 'white',
    fontSize: theme.fontSize.lg,
    fontWeight: theme.fontWeight.semibold,
    textAlign: 'center',
    padding: theme.spacing.lg,
});

// Current step highlight card
export const CurrentStepCard = styled.div<{ stepColor?: string }>((props) => ({
    backgroundColor: theme.surfaces.card,
    borderRadius: theme.borderRadius.md,
    padding: theme.spacing.md,
    boxShadow: theme.shadows.sm,
    borderLeft: `3px solid ${props.stepColor || theme.colors.primary[600]}`,
}));

export const CurrentStepTitle = styled.div({
    fontSize: theme.fontSize.sm,
    fontWeight: theme.fontWeight.semibold,
    color: theme.colors.gray[800],
    marginBottom: theme.spacing.xs,
});

export const CurrentStepDescription = styled.div({
    fontSize: theme.fontSize.xs,
    color: theme.colors.gray[600],
});

// Velocity and result row (combined for space efficiency)
export const VelocityResultRow = styled.div({
    display: 'flex',
    gap: theme.spacing.md,
    alignItems: 'flex-start',

    [`@media (max-width: ${theme.breakpoints.md})`]: {
        flexDirection: 'column',
    },
});

export const VelocityInput = styled.div({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xs,
    minWidth: '100px',
});

export const ResultSection = styled.div({
    flex: 1,
});

// Status badges for completed steps
export const CompletedBadge = styled.span({
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
    backgroundColor: theme.colors.green[100],
    color: theme.colors.green[700],
    borderRadius: theme.borderRadius.full,
    fontSize: theme.fontSize.xs,
    fontWeight: theme.fontWeight.medium,
});

export const PendingBadge = styled.span({
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
    backgroundColor: theme.colors.gray[100],
    color: theme.colors.gray[500],
    borderRadius: theme.borderRadius.full,
    fontSize: theme.fontSize.xs,
    fontWeight: theme.fontWeight.medium,
});

// Pitch type filter for heat zones
export const HeatZonePitchFilter = styled.div({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing.xs,
    marginBottom: theme.spacing.sm,
});

export const HeatZonePitchButton = styled.button<{ active?: boolean }>((props) => ({
    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
    backgroundColor: props.active ? theme.colors.primary[600] : theme.colors.gray[100],
    color: props.active ? 'white' : theme.colors.gray[700],
    border: `1px solid ${props.active ? theme.colors.primary[600] : theme.colors.gray[300]}`,
    borderRadius: theme.borderRadius.md,
    fontSize: theme.fontSize.xs,
    fontWeight: theme.fontWeight.medium,
    cursor: 'pointer',
    transition: 'all 0.15s',

    '&:hover': {
        backgroundColor: props.active ? theme.colors.primary[700] : theme.colors.gray[200],
    },
}));

// Role selection overlay
export const RoleSelectOverlay = styled.div({
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 200,
});

export const RoleSelectCard = styled.div({
    background: theme.surfaces.card,
    borderRadius: theme.borderRadius.xl,
    padding: theme.spacing['3xl'],
    boxShadow: theme.shadows.xl,
    textAlign: 'center',
    minWidth: 320,
});

export const RoleSelectTitle = styled.h2({
    fontSize: theme.fontSize['2xl'],
    fontWeight: theme.fontWeight.bold,
    color: theme.colors.gray[900],
    marginBottom: theme.spacing.sm,
});

export const RoleSelectSubtitle = styled.p({
    fontSize: theme.fontSize.sm,
    color: theme.colors.gray[500],
    marginBottom: theme.spacing['2xl'],
});

export const RoleSelectButtons = styled.div({
    display: 'flex',
    gap: theme.spacing.md,
    justifyContent: 'center',
});

export const RoleButton = styled.button({
    padding: `${theme.spacing.sm} ${theme.spacing['2xl']}`,
    background: theme.colors.primary[600],
    color: 'white',
    border: 'none',
    borderRadius: theme.borderRadius.lg,
    fontSize: theme.fontSize.base,
    fontWeight: theme.fontWeight.semibold,
    cursor: 'pointer',
    transition: 'background 0.15s',

    '&:hover': {
        background: theme.colors.primary[700],
    },
});

export const DroppedThirdOverlay = styled.div({
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
});

export const DroppedThirdDialog = styled.div({
    backgroundColor: theme.surfaces.card,
    borderRadius: theme.borderRadius.xl,
    padding: theme.spacing['2xl'],
    width: 320,
    boxShadow: theme.shadows.xl,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing.md,
});

export const DroppedThirdTitle = styled.h3({
    margin: 0,
    fontSize: theme.fontSize.lg,
    fontWeight: theme.fontWeight.bold,
    color: theme.colors.gray[900],
});

export const DroppedThirdMessage = styled.p({
    margin: 0,
    fontSize: theme.fontSize.base,
    color: theme.colors.gray[600],
    textAlign: 'center',
});

export const DroppedThirdButtons = styled.div({
    display: 'flex',
    gap: theme.spacing.md,
    marginTop: theme.spacing.sm,
});

export const DroppedThirdBtn = styled.button<{ variant: 'yes' | 'no' }>(({ variant }) => ({
    padding: `${theme.spacing.sm} ${theme.spacing['2xl']}`,
    borderRadius: theme.borderRadius.md,
    fontSize: theme.fontSize.base,
    fontWeight: theme.fontWeight.semibold,
    cursor: 'pointer',
    border: 'none',
    ...(variant === 'yes'
        ? {
              backgroundColor: theme.colors.green[600],
              color: 'white',
              '&:hover': { backgroundColor: theme.colors.green[700] },
          }
        : {
              backgroundColor: theme.colors.gray[200],
              color: theme.colors.gray[700],
              '&:hover': { backgroundColor: theme.colors.gray[300] },
          }),
}));

// --- Settings panel (UX-LG-24 cleanup) ---
export const SettingsAnchor = styled.div({
    position: 'relative',
});

export const SettingsPanel = styled.div({
    position: 'absolute',
    top: '110%',
    right: 0,
    background: theme.surfaces.card,
    border: `1px solid ${theme.colors.gray[200]}`,
    borderRadius: '10px',
    boxShadow: theme.shadows.md,
    padding: '14px 18px',
    minWidth: '220px',
    zIndex: 200,
});

export const SettingsPanelTitle = styled.div({
    fontWeight: 700,
    fontSize: '13px',
    color: theme.colors.gray[700],
    marginBottom: '12px',
});

export const SettingsRow = styled.label({
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    marginBottom: '10px',
    cursor: 'pointer',
});

export const SettingsCheckbox = styled.input({
    marginTop: '2px',
    cursor: 'pointer',
});

export const SettingsRowLabel = styled.div({
    fontSize: '13px',
    fontWeight: 600,
    color: theme.colors.gray[800],
});

export const SettingsRowSub = styled.div({
    fontSize: '11px',
    color: theme.colors.gray[500],
});
